"use client";

import React from "react";
import { Building2, MapPin, PhoneCall, Truck, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactHubs = [{
  id: "hq",
  title: "Centrála",
  description: "456 Elm Street, Kancelář 300, VašeMěsto, PSČ",
  icon: Building2,
  actionText: "Získat trasu",
  actionLink: "#",
  highlight: false
}, {
  id: "dispatch",
  title: "Servisní dispečink",
  description: "contact@nexotechhvac.com",
  icon: Truck,
  actionText: "Napsat dispečinku",
  actionLink: "mailto:contact@nexotechhvac.com",
  highlight: false
}, {
  id: "emergency",
  title: "Pohotovostní linka",
  description: "+1 (555) 987-6543",
  icon: PhoneCall,
  actionText: "Volejte 24/7 linku",
  actionLink: "tel:+15559876543",
  highlight: true
}];
const mapMarkers = [{
  id: 1,
  top: "30%",
  left: "25%"
}, {
  id: 2,
  top: "45%",
  left: "60%"
}, {
  id: 3,
  top: "65%",
  left: "40%"
}, {
  id: 4,
  top: "20%",
  left: "75%"
}, {
  id: 5,
  top: "75%",
  left: "80%"
}];
export default function GlobalPresence() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="global-presence" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px] pointer-events-none" />

      <div className="max-w-screen-xl mx-auto px-4 md:px-6 relative z-10">
        <div ref={headerRef} className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4 text-foreground">
            Globální <span className="text-primary">přítomnost</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Strategická servisní centra rozmístěná tak, aby zajišťovala rychlou odezvu a komplexní inženýrskou podporu v celém našem operačním prostoru.
          </p>
        </div>

        <div ref={contentRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 items-start transition-all duration-1000 delay-200 transform ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="lg:col-span-4 flex flex-col gap-6">
            {contactHubs.map((hub, i) => {
            const Icon = hub.icon;
            return <Card key={hub.id} data-index={i} className={`group relative overflow-hidden border-border/50 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${hub.highlight ? "bg-card border-secondary/30 shadow-[0_0_30px_-10px_rgba(255,136,0,0.2)]" : "bg-card/50 backdrop-blur-sm hover:bg-card"}`}>
                  {hub.highlight && <div className="absolute top-0 left-0 w-1 h-full bg-secondary" />}
                  {!hub.highlight && <div className="absolute top-0 left-0 w-1 h-full bg-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />}
                  
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-lg ${hub.highlight ? "bg-secondary/10 text-secondary" : "bg-primary/10 text-primary"}`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <CardTitle className="text-xl">{hub.title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-muted-foreground mb-6 min-h-[3rem]">
                      {hub.description}
                    </CardDescription>
                    <Button asChild variant={hub.highlight ? "secondary" : "outline"} className={`w-full justify-between group/btn ${!hub.highlight && "hover:bg-primary hover:text-primary-foreground hover:border-primary"}`}>
                      <Link to={hub.actionLink}>
                        {hub.actionText}
                        <ArrowRight className="w-4 h-4 transition-transform group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>;
          })}
          </div>

          {}
          <div className="lg:col-span-8 relative h-[500px] lg:h-full min-h-[500px] rounded-2xl overflow-hidden border border-border/50 shadow-2xl group">
            {}
            <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1582043/img/dark_map_hvac_hubs.png)] bg-cover bg-center opacity-80 transition-transform duration-1000 group-hover:scale-105" />
            
            {}
            <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
            <div className="absolute inset-0 bg-primary/5 mix-blend-overlay" />

            {}
            {mapMarkers.map((marker, i) => <div key={marker.id} data-index={i} className="absolute transform -translate-x-1/2 -translate-y-1/2" style={{
            top: marker.top,
            left: marker.left
          }}>
                <div className="relative flex items-center justify-center">
                  <div className="absolute w-8 h-8 bg-primary/30 rounded-full animate-ping" />
                  <div className="absolute w-4 h-4 bg-primary/40 rounded-full animate-pulse" />
                  <MapPin className="w-6 h-6 text-primary relative z-10 drop-shadow-[0_0_8px_rgba(0,163,255,0.8)]" />
                </div>
              </div>)}

            {}
            <div className="absolute bottom-6 left-6 right-6 md:right-auto md:w-80 bg-card/90 backdrop-blur-md border border-border/50 p-4 rounded-xl shadow-lg">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-sm font-medium text-foreground">Živá síť dispečinku</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Monitorování 5 aktivních servisních regionů s trasováním techniků v reálném čase přes NEXO CORE.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}